// useConfig — one-shot fetch of /config, cached for the rest of the session.
//
// The /config payload (github owner/repo, queue mode, configured providers)
// is read-only and only changes when the platform redeploys, so we never
// re-fetch within a session. Callers receive `null` while the first request
// is in flight and a cached object thereafter.

import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import type { ConfigResponse } from '../lib/types';

let cachedConfig: ConfigResponse | null = null;
let inflight: Promise<ConfigResponse> | null = null;
const subscribers = new Set<(c: ConfigResponse | null) => void>();

function fetchOnce(): Promise<ConfigResponse> {
  if (cachedConfig) return Promise.resolve(cachedConfig);
  if (inflight) return inflight;
  inflight = api.getConfig().then((c) => {
    cachedConfig = c;
    inflight = null;
    for (const sub of subscribers) sub(c);
    return c;
  }).catch((err) => {
    inflight = null;
    throw err;
  });
  return inflight;
}

// Test seam — vitest tests reset the cache between cases.
export function __resetConfigCache(): void {
  cachedConfig = null;
  inflight = null;
}

export interface UseConfigResult {
  config: ConfigResponse | null;
  error: string | null;
}

export function useConfig(): UseConfigResult {
  const [config, setConfig] = useState<ConfigResponse | null>(cachedConfig);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const onUpdate = (c: ConfigResponse | null): void => {
      if (active) setConfig(c);
    };
    subscribers.add(onUpdate);
    fetchOnce().then(
      (c) => { if (active) setConfig(c); },
      (err) => { if (active) setError(err instanceof Error ? err.message : String(err)); },
    );
    return () => {
      active = false;
      subscribers.delete(onUpdate);
    };
  }, []);

  return { config, error };
}
